import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ArrowLeft, ChevronLeft, ChevronRight, FileClock, Loader2, Shield } from 'lucide-react'
import { useTeamStore } from '../store/useTeamStore'
import ProfileLink from '../../../components/ProfileLink'
import { auditActionText, personName } from '../utils/teamFormat'
import { accentColor } from '../utils/teamColors'
import { formatAgo } from '../../profile/utils/profileFormat'
import { CARD_CLASS, SECTION_VARIANT, SECTIONS_VARIANT } from '../utils/teamUi'

const PAGE_SIZE = 50

/**
 * Аудит-лог банды (`/teams/audit`) — доступен любому участнику, новые сверху.
 *
 * Бэкенд отдаёт только ключ действия и участников, фраза собирается на фронте
 * (`auditActionText`). `actor: null` — действие администрации: имя сотрудника
 * наружу не отдаётся никогда, поэтому такие события рисуются без ссылки на
 * профиль. Голоса и подача заявок в аудит намеренно не пишутся.
 */
const TeamAudit = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { team, audit, isAuditLoading, loadTeam, loadAudit } = useTeamStore()
  const [page, setPage] = useState(1)

  useEffect(() => {
    loadTeam('my')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadAudit(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const accent = accentColor(team?.colors)
  const hasNextPage = audit.events.length === PAGE_SIZE

  return (
    <div className="mx-auto w-full px-4 py-6 lg:max-w-3xl lg:px-6 lg:py-8">
      <motion.div variants={SECTIONS_VARIANT} initial="hidden" animate="visible" className="space-y-5">
        <motion.div variants={SECTION_VARIANT} className="flex items-center gap-3">
          <button
            onClick={() => navigate('/teams/my')}
            className="rounded-lg bg-surface p-2 text-white transition-all active:scale-[0.98]"
            aria-label={t('common.back', 'Назад')}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl text-white">{t('team.auditTitle', 'История банды')}</h1>
        </motion.div>

        <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-4`}>
          {isAuditLoading && audit.events.length === 0 ? (
            <div className="flex justify-center py-8">
              <Loader2 size={20} className="animate-spin text-accent-primary" />
            </div>
          ) : audit.events.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-dark-primary">
                <FileClock size={24} className="text-text-secondary" />
              </span>
              <p className="text-sm text-text-secondary">
                {t('team.auditEmpty', 'Событий пока нет')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {audit.events.map((event) => {
                const isAdmin = !event.actor
                const actorName = personName(event.actor, t)

                return (
                  <div key={event.id} className="flex items-start gap-3 rounded-xl border border-white/5 bg-dark-primary p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                    {isAdmin ? (
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-surface">
                        <Shield size={16} className="text-text-secondary" />
                      </span>
                    ) : (
                      <ProfileLink slug={event.actor.slug} className="shrink-0">
                        <span className="block h-9 w-9 overflow-hidden rounded-full border border-white/10 bg-surface">
                          {event.actor.ava ? (
                            <img src={event.actor.ava} alt={actorName} className="h-full w-full object-cover" />
                          ) : null}
                        </span>
                      </ProfileLink>
                    )}

                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm text-white">
                        {!isAdmin && (
                          <>
                            <ProfileLink slug={event.actor.slug} className="font-semibold" style={{ color: accent }}>
                              {actorName}
                            </ProfileLink>{' '}
                          </>
                        )}
                        {auditActionText(event, t)}
                      </p>
                      <p className="mt-0.5 text-[11px] text-text-secondary">{formatAgo(event.ago, t)}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {(page > 1 || hasNextPage) && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isAuditLoading}
                className="rounded-xl border border-white/10 bg-surface p-2.5 text-white transition-all active:scale-[0.98] disabled:opacity-40"
                aria-label={t('common.back', 'Назад')}
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm tabular-nums text-text-secondary">{page}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNextPage || isAuditLoading}
                className="rounded-xl border border-white/10 bg-surface p-2.5 text-white transition-all active:scale-[0.98] disabled:opacity-40"
                aria-label={t('team.nextPage', 'Дальше')}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </div>
  )
}

export default TeamAudit
